import { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import type { EntityRow, Verdict } from '../lib/types';
import { TableOrCards, type Column } from './TableOrCards';
import { ScorePill } from './ScorePill';
import { EntityTypeIcon } from './EntityTypeIcon';
import { formatScore } from '../lib/format';
import styles from './EntityTable.module.css';

interface Props {
  rows: EntityRow[];
  className?: string;
}

type SortKey = 'name' | 'type' | 'sentiment' | 'score';

const SORT_LABELS: Record<SortKey, string> = {
  name: 'Name',
  type: 'Type',
  sentiment: 'Sentiment',
  score: 'Score',
};

// BEARISH < MIXED < BULLISH so the sentiment sort is meaningful (most-bearish
// to most-bullish, or vice versa) rather than alphabetical.
const VERDICT_ORDER: Record<Verdict, number> = {
  BEARISH: -1,
  MIXED: 0,
  BULLISH: 1,
};

export function EntityTable({ rows, className }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'score':     cmp = a.score - b.score; break;
        case 'name':      cmp = a.name.localeCompare(b.name); break;
        case 'type':      cmp = a.type.localeCompare(b.type); break;
        case 'sentiment': cmp = VERDICT_ORDER[a.verdict] - VERDICT_ORDER[b.verdict]; break;
      }
      // Stable secondary sort by score, descending — so two BULLISH entities
      // with the same alphabetical/type tie-break by their actual magnitude.
      if (cmp === 0 && sortKey !== 'score') cmp = b.score - a.score;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const onSort = (k: SortKey) => {
    if (sortKey === k) {
      // Same column → flip direction.
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      // New column → name/type default ascending; sentiment/score default descending.
      setSortKey(k);
      setSortDir(k === 'name' || k === 'type' ? 'asc' : 'desc');
    }
  };

  const columns: Column<EntityRow>[] = [
    {
      key: 'name',
      header: 'Entity',
      render: (r) => (
        <span className={styles.entityCell}>
          <EntityTypeIcon type={r.type} />
          <span className={styles.entityName}>{r.name}</span>
        </span>
      ),
    },
    { key: 'type', header: 'Type', render: (r) => <span className={styles.typePill}>{r.type}</span> },
    {
      key: 'verdict',
      header: 'Sentiment',
      render: (r) => <ScorePill verdict={r.verdict} size="sm" />,
    },
    {
      key: 'score',
      header: 'Score',
      align: 'right',
      render: (r) => <span className="mono">{formatScore(r.score)}</span>,
    },
    { key: 'context', header: 'Context', render: (r) => <span className={styles.context}>{r.context}</span> },
  ];

  return (
    <div className={className}>
      <div className={styles.sortBar} role="toolbar" aria-label="Sort entity table">
        <span className={styles.sortLabel}>Sort</span>
        {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => {
          const isActive = sortKey === k;
          return (
            <button
              key={k}
              type="button"
              className={[styles.sortBtn, isActive ? styles.sortActive : ''].join(' ')}
              onClick={() => onSort(k)}
              aria-pressed={isActive}
              title={isActive
                ? `Sorted by ${SORT_LABELS[k]} (${sortDir === 'asc' ? 'ascending' : 'descending'}). Click to flip.`
                : `Sort by ${SORT_LABELS[k]}`}
            >
              <span>{SORT_LABELS[k]}</span>
              {isActive && (
                sortDir === 'asc'
                  ? <ArrowUp size={12} strokeWidth={2.25} aria-hidden="true" />
                  : <ArrowDown size={12} strokeWidth={2.25} aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
      <TableOrCards
        ariaLabel="Entity sentiment"
        columns={columns}
        rows={sorted}
        rowKey={(r) => r.name}
        cardTitle={(r) => (
          <span className={styles.cardTitleWrap}>
            <EntityTypeIcon type={r.type} />
            <span>{r.name}</span>
          </span>
        )}
        cardMeta={(r) => <ScorePill verdict={r.verdict} size="sm" />}
      />
    </div>
  );
}
